import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useParams, useSearchParams} from "react-router";
import {Stack, Typography} from "@mui/material";
import {getReportById} from "../../../../firebase/firestore/reports";
import {loadSnapshotData} from "../../../../utils/exportReportHelper";
import {formatTimestamp} from '../../../../utils/formatTimestamp';
import LoadingInTheMiddle from "../../../../components/LoadingInTheMiddle";
import NodeRenderer from "../../pages/page/previewPage/NodeRenderer";

const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() {
    const t = localStorage.getItem('access_token');
    return t ? {'Authorization': `Bearer ${t}`} : {};
}

const ProductPreviewSection = () => {
    const {sourceSegmentId} = useParams();
    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const highlightedRef = useRef(null);
    const contentRef = useRef(null);
    const [searchParams] = useSearchParams();
    const searchText = useMemo(() => {
        const q = searchParams.get('q');
        return q ? decodeURIComponent(q) : null;
    }, [searchParams]);

    // Parse sourceSegmentId -> reportId + nestedIndexesTree (matches indexPath from semantization)
    const {reportId, nestedIndexesTree} = useMemo(() => {
        if (!sourceSegmentId) return {reportId: null, nestedIndexesTree: []};

        const parts = sourceSegmentId.split('-').filter(Boolean);
        if (parts.length === 0) return {reportId: null, nestedIndexesTree: []};

        const id = parts[0];
        const path = parts
            .slice(1)
            .map((idx) => {
                const n = Number(idx);
                return Number.isNaN(n) ? null : n;
            })
            .filter((v) => v !== null);

        return {reportId: id, nestedIndexesTree: path};
    }, [sourceSegmentId]);

    // Load report header from Firestore and content from Storage snapshot via cloud function.
    useEffect(() => {
        let mounted = true;

        if (!reportId) {
            setReport(null);
            return;
        }

        const loadReport = async () => {
            setIsLoading(true);
            try {
                const reportData = await getReportById(reportId);
                if (!mounted || !reportData) {
                    if (mounted) setReport(reportData);
                    return;
                }

                let snapshotContent = null;
                let snapshotMetadata = null;

                if (reportData.snapshotPath) {
                    try {
                        const snapshotData = await loadSnapshotData(reportId, reportData.snapshotPath);
                        snapshotContent = snapshotData?.content || null;
                        snapshotMetadata = snapshotData?.metadata || null;
                    } catch (snapshotError) {
                        // Keep rendering report shell even if snapshot decode fails.
                        console.error('Error loading report snapshot:', snapshotError);
                    }
                }

                // Fallback: load pm_json directly from the backend when snapshot is unavailable
                if (!snapshotContent && !reportData.content) {
                    try {
                        const resp = await fetch(`${_awpBase}/api/reports/${encodeURIComponent(reportId)}/content`, {
                            headers: _authH(),
                        });
                        if (resp.ok) {
                            const data = await resp.json();
                            if (data?.content) snapshotContent = data.content;
                        }
                    } catch (e) {
                        console.warn('Could not load report content fallback', e);
                    }
                }

                if (!mounted) return;
                setReport({
                    ...reportData,
                    content: snapshotContent || reportData.content || null,
                    snapshotMetadata,
                });
            } catch (error) {
                console.error('Error loading report:', error);
            } finally {
                if (mounted) setIsLoading(false);
            }
        };

        loadReport();

        return () => {
            mounted = false;
        };
    }, [reportId]);

    // Highlight + scroll when content ready and nestedIndexesTree changes (including on page load)
    useEffect(() => {
        if (isLoading) return;
        if (!report) return;
        if (!nestedIndexesTree || nestedIndexesTree.length === 0) return;

        // Wait one frame to ensure NodeRenderer has attached the ref
        const handle = window.requestAnimationFrame(() => {
            if (highlightedRef.current) {
                highlightedRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        });

        return () => window.cancelAnimationFrame(handle);
    }, [nestedIndexesTree, report, isLoading]);

    // Text-based highlight: scroll to the first DOM node whose text contains searchText
    useEffect(() => {
        if (!searchText || !report || isLoading) return;
        if (!contentRef.current) return;

        const timer = setTimeout(() => {
            if (!contentRef.current) return;
            const walker = document.createTreeWalker(
                contentRef.current,
                NodeFilter.SHOW_TEXT,
                null
            );
            const needle = searchText.slice(0, 40);
            let node;
            while ((node = walker.nextNode())) {
                if (node.textContent.includes(needle)) {
                    const el = node.parentElement;
                    if (!el) break;
                    // Flash yellow and scroll
                    const prev = el.style.backgroundColor;
                    el.style.transition = 'background-color 0.3s ease';
                    el.style.backgroundColor = '#FFD54F';
                    el.scrollIntoView({behavior: 'smooth', block: 'center'});
                    setTimeout(() => {
                        el.style.backgroundColor = prev;
                    }, 3500);
                    break;
                }
            }
        }, 700);

        return () => clearTimeout(timer);
    }, [searchText, report, isLoading]);

    if (isLoading) {
        return <LoadingInTheMiddle/>;
    }

    if (!sourceSegmentId) {
        return (
            <Stack spacing={1} p={2} alignItems="center" justifyContent="center" height="100%">
                <Typography color="text.secondary">Select a source segment to preview</Typography>
            </Stack>
        );
    }

    if (!report) {
        return (
            <Stack spacing={1} p={2} alignItems="center" justifyContent="center" height="100%">
                <Typography color="text.secondary">Report not found</Typography>
            </Stack>
        );
    }

    const content = report.content;
    const meta = report.snapshotMetadata || report.meta;

    return (
        <Stack spacing={1} p={4}>
            {/* Report Header */}
            <Typography variant="h5" component="h1" sx={{fontWeight: 700}}>
                {meta?.title || report.title || 'Untitled Report'}
            </Typography>

            {/* Report Meta Information */}
            {meta && (
                <Stack spacing={0.5} p={1.5} bgcolor={'#f9f9f9'} borderRadius={1} mb={1}>
                    {meta.dtgIso && (
                        <Typography variant="caption">
                            DTG (UTC): {formatTimestamp(meta.dtgIso)}
                        </Typography>
                    )}
                    {meta.classification && (
                        <Typography variant="caption">
                            Classification: {meta.classification.label || meta.classification.value || 'N/A'}
                        </Typography>
                    )}
                </Stack>
            )}

            {/* Report Content */}
            {content && content.type === 'doc' && Array.isArray(content.content) && (
                <Stack spacing={1} ref={contentRef}>
                    {content.content.map((node, i) => (
                        <NodeRenderer
                            key={i}
                            node={node}
                            idx={i}
                            parentPath={[]}
                            nestedIndexesTree={nestedIndexesTree}
                            highlightedRef={highlightedRef}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

export default ProductPreviewSection;

